import React from 'react';
import { useTranslation } from 'react-i18next';
import { Shuffle, Star, Delete } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface QuickPicksModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  description?: string;
  confirmText?: string;
  type?: 'quick' | 'fav' | 'clear';
  cancelText?: string;
}

/**
 * Bottom-sheet confirmation — Figma drawer 10203:48877
 * Quick Pick / Fav Picks / Clear Picks.
 */
const QuickPicksModal: React.FC<QuickPicksModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText,
  type = 'quick',
  cancelText,
}) => {
  const { t } = useTranslation();

  const resolvedTitle = title ?? t('modal.quickPick.title');
  const resolvedDescription = description ?? t('modal.quickPick.description');
  const resolvedConfirmText = confirmText ?? t('modal.quickPick.confirm');
  const resolvedCancelText = cancelText ?? t('common.cancel');

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const icon =
    type === 'fav' ? (
      <Star className="size-10 text-white" strokeWidth={1.5} />
    ) : type === 'clear' ? (
      <Delete className="size-10 text-white" strokeWidth={1.5} />
    ) : (
      <Shuffle className="size-10 text-white" strokeWidth={1.5} />
    );

  return (
    <Dialog
      open={isOpen}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <DialogContent data-test-id="quick-picks-modal" className="items-center">
        <div className="flex size-10 shrink-0 items-center justify-center text-white" aria-hidden>
          {icon}
        </div>

        <DialogHeader>
          <DialogTitle>{resolvedTitle}</DialogTitle>
          <DialogDescription>{resolvedDescription}</DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button
            data-test-id="confirm-btn"
            variant="primary"
            shape="pill"
            size="lg"
            onClick={handleConfirm}
            className="w-full min-h-12"
            showTrailingIcon={false}
          >
            {resolvedConfirmText}
          </Button>
          <Button
            data-test-id="cancel-btn"
            variant="link"
            onClick={onClose}
            className="min-h-12 underline decoration-solid"
            showTrailingIcon={false}
          >
            {resolvedCancelText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuickPicksModal;
